import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { Transition, Variants, motion, useAnimationControls } from 'framer-motion'
import React, { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { BaseModalType } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useArabicSessionLanguage, useSessionLanguage } from '@context/SessionSettingsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Title1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { AlertNotice } from '@ui/components/AlertNotice'
import { Button, IconButton } from '@ui/components/Button'
import { Divider } from '@ui/components/Divider'
import { Modal } from '@ui/components/Modal'
import { useAnimatedModalClose } from '@ui/components/Modal/hooks/useAnimatedModalClose'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { CloseIcon } from '@ui/icons/Close.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { UndoUpLeftIcon } from '@ui/icons/UndoUpLeft.icon'
import { UndoUpRightIcon } from '@ui/icons/UndoUpRight.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate, withTimeFormat } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import {
  TransactionContext,
  TransactionFields,
  TransactionType,
  getCreditTransaction,
  getDebitTransaction,
  getTransactionDisplay,
  getTransactionStatusAlertMessage,
  getTransactionStatusLastUpdatedDate,
} from '@utils/transaction.utils'

import { isPaymentProviderCryptoType } from '@model/PaymentProviderType'
import {
  TradingAccountTransactionDto,
  TransactionDto,
  WalletTransactionDto,
} from '@model/TransactionDto'
import { TransactionStatus } from '@model/TransactionStatus'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useRelativeTime } from '@hooks/useRelativeTime'

import { getCardByCardType } from '@assets/cards'

import { AnimatedHeight } from '@shared/animations/AnimatedHeight'
import { AnimatedNumber } from '@shared/animations/AnimatedNumber'
import { AnimatedSwitch } from '@shared/animations/AnimatedSwitch'
import { Box } from '@shared/components/Box'
import { CopyButton } from '@shared/components/CopyButton/CopyButton'
import { getFundingCurrencyIcon } from '@shared/components/Funding/FundingCurrencyIcons'
import { Loading } from '@shared/components/Loading/Loading'
import {
  TransactionHistoryFromToCell,
  getFromToCellData,
  normalizeFromToCellContent,
} from '@shared/components/TransactionHistoryFromToCell'
import { TransactionStatusItem } from '@shared/components/TransactionStatusItem'

import styles from './TransactionHistory.drawer.modal.module.scss'

interface Props {
  initialTransactionId: string
  transactions: TransactionDto[]
  context: TransactionContext
  walletId?: string
  onCancelTransaction?: (tx: TransactionDto) => void
}

interface OriginalAmountData {
  amount: number
  currency: CurrencyType
}

interface WithdrawalDetails {
  bankCode?: string
  bankName?: string
  externalAccount?: string
  isCrypto: boolean
}

const ENTER_TRANSITION: Transition = { type: 'spring', stiffness: 350, damping: 36 }
const EXIT_TRANSITION: Transition = {
  type: 'tween',
  duration: 0.22,
  ease: [0.2, 0.0, 0.2, 1],
}

const getDrawerVariantsDesktop = (isRtl: boolean): Variants => {
  const offscreen = isRtl ? '-100%' : '100%'

  return {
    initial: { x: offscreen },
    animate: { x: '0%', transition: ENTER_TRANSITION },
    exit: { x: offscreen, transition: EXIT_TRANSITION },
  }
}

const drawerVariantsMobile: Variants = {
  initial: { y: '100%' },
  animate: { y: '0%', transition: ENTER_TRANSITION },
  exit: { y: '100%', transition: EXIT_TRANSITION },
}

type TransactionLeg = WalletTransactionDto | TradingAccountTransactionDto | undefined

const isWalletLeg = (leg: TransactionLeg): leg is WalletTransactionDto =>
  !!leg && 'wallet' in leg && !!leg.wallet

const getLegCurrency = (leg: TransactionLeg): CurrencyType | undefined =>
  isWalletLeg(leg) ? leg.wallet?.currency?.id : leg?.currency?.id

const getLegOtherCurrency = (leg: TransactionLeg): CurrencyType | undefined =>
  leg?.otherCurrency?.id

const getNonEmptyValue = (value: string | null | undefined): string | undefined => {
  return value?.trim() ? value : undefined
}

const getWithdrawalDetails = (transaction: TransactionDto): WithdrawalDetails | null => {
  if (transaction.type.id !== TransactionType.WalletWithdrawal) {
    return null
  }

  const paymentProviderTransaction = transaction.walletTransactions[0]?.paymentProviderTransaction
  const bankCode = getNonEmptyValue(paymentProviderTransaction?.request?.bankCode)
  const bankName = getNonEmptyValue(paymentProviderTransaction?.request?.bankName)
  const externalAccount = getNonEmptyValue(paymentProviderTransaction?.request?.externalAccount)

  if (!bankName && !externalAccount) {
    return null
  }

  return {
    bankCode,
    bankName,
    externalAccount,
    isCrypto: isPaymentProviderCryptoType(paymentProviderTransaction?.paymentProvider.category.id),
  }
}

const getOriginalAmountData = (
  creditTx: TransactionLeg,
  debitTx: TransactionLeg,
  fields: TransactionFields
): OriginalAmountData | null => {
  const creditOtherAmount = Number(creditTx?.otherAmount)
  const creditOtherCurrency = getLegOtherCurrency(creditTx)
  if (
    creditOtherCurrency &&
    Number.isFinite(creditOtherAmount) &&
    !(creditOtherCurrency === fields.currency && creditOtherAmount === fields.amount)
  ) {
    return { amount: creditOtherAmount, currency: creditOtherCurrency }
  }

  const debitOtherAmount = Number(debitTx?.otherAmount)
  const debitOtherCurrency = getLegOtherCurrency(debitTx)
  if (
    debitOtherCurrency &&
    Number.isFinite(debitOtherAmount) &&
    !(debitOtherCurrency === fields.currency && debitOtherAmount === fields.amount)
  ) {
    return { amount: debitOtherAmount, currency: debitOtherCurrency }
  }

  const debitAmount = Number(debitTx?.amount)
  const debitCurrency = getLegCurrency(debitTx)
  if (
    debitCurrency &&
    Number.isFinite(debitAmount) &&
    !(debitCurrency === fields.currency && debitAmount === fields.amount)
  ) {
    return { amount: debitAmount, currency: debitCurrency }
  }

  return null
}

export const TransactionHistoryDrawerModal: BaseModalType<Props> = ({
  initialTransactionId,
  transactions,
  context,
  walletId,
  onCancelTransaction,
}) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const { account } = useAccountReadContext()
  const dateFormat = auth?.dateFormatType?.name
  const locale = useSessionLanguage()
  const isArabic = useArabicSessionLanguage()
  const { isDefaultCFDProductType } = useProductReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const { formatMoney, formatNumber } = useFormatNumber()
  const controls = useAnimationControls()
  const isMobile = useWindowResize()
  const drawerVariants = isMobile ? drawerVariantsMobile : getDrawerVariantsDesktop(isArabic)

  const [currentTransactionId, setCurrentTransactionId] = useState(initialTransactionId)

  useLayoutEffect(() => {
    controls.set('initial')
    controls.start('animate')
  }, [controls])

  const requestClose = useAnimatedModalClose(() => controls.start('exit'))

  useEffect(() => {
    if (!transactions.length) return
    const exists = transactions.some((tx) => tx.id === currentTransactionId)
    if (!exists) {
      setCurrentTransactionId(transactions[0].id)
    }
  }, [currentTransactionId, transactions])

  const currentIndex = useMemo(
    () => transactions.findIndex((tx) => tx.id === currentTransactionId),
    [currentTransactionId, transactions]
  )

  const transaction = transactions[currentIndex] || transactions[0]

  const { data: statusHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: queryKeys.transaction.statusHistory(transaction?.id, locale),
    queryFn: () => apiClient.getTransactionStatusHistory(transaction.id, locale),
    enabled: !!transaction?.id,
  })

  const statusLastUpdatedDate = getTransactionStatusLastUpdatedDate(transaction, statusHistory)
  const relativeLastUpdate = useRelativeTime(statusLastUpdatedDate)

  const timelineItems = useMemo(
    () =>
      [...(statusHistory?.history ?? [])].sort(
        (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
      ),
    [statusHistory]
  )

  if (!transaction) {
    return (
      <motion.div
        className={styles.drawer}
        variants={drawerVariants}
        initial='initial'
        animate={controls}
      >
        {isMobile ? (
          <Box py='x12' borderBottom='outline-base-em' flexItem='0 0 auto'>
            <Modal.Header canClose>{t('Transaction details')}</Modal.Header>
          </Box>
        ) : (
          <Box
            flex
            align='center'
            justify='space-between'
            gap='x8'
            py='x12'
            px='x20'
            borderBottom='outline-low-em'
            flexItem='0 0 auto'
          >
            <Title1 medium>{t('Transaction details')}</Title1>
            <IconButton.Secondary size='sm' icon={CloseIcon} onClick={requestClose} />
          </Box>
        )}
      </motion.div>
    )
  }

  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < transactions.length - 1

  const { isReversed, fields, amountSign } = getTransactionDisplay(transaction, context, {
    isArabic,
    authId: account?.id,
    isDefaultCFDProductType: isDefaultCFDProductType(),
    walletId,
  })

  const isCancellable =
    !!onCancelTransaction &&
    (transaction.type.id === TransactionType.WalletWithdrawal ||
      transaction.type.id === TransactionType.WithdrawToPaymentAgent) &&
    transaction.state.id === TransactionStatus.AwaitingApproval

  const fromDirection = isReversed ? 'to' : 'from'
  const toDirection = isReversed ? 'from' : 'to'
  const fromCellData = getFromToCellData(transaction, fromDirection)
  const toCellData = getFromToCellData(transaction, toDirection)
  const fromCellContent = normalizeFromToCellContent(
    transaction.type.id,
    fromDirection,
    fields.from,
    fromCellData.subtitle
  )
  const toCellContent = normalizeFromToCellContent(
    transaction.type.id,
    toDirection,
    fields.to,
    toCellData.subtitle
  )

  const creditTx = getCreditTransaction(transaction.type.id, transaction)
  const debitTx = getDebitTransaction(transaction.type.id, transaction)

  const exchangeRateCandidate = creditTx?.currencyRate ?? debitTx?.currencyRate ?? fields.rate
  const exchangeRate =
    exchangeRateCandidate && exchangeRateCandidate !== 1 ? exchangeRateCandidate : null

  const originalAmount = getOriginalAmountData(creditTx, debitTx, fields)
  const statusAlert = getTransactionStatusAlertMessage(transaction.state.id)

  const cardDetails = transaction.cardDetails
  const CardBrandIcon = getCardByCardType(cardDetails?.cardType.id)
  const withdrawalDetails = getWithdrawalDetails(transaction)
  const DigitalCurrencyIcon =
    withdrawalDetails?.isCrypto && withdrawalDetails.bankCode
      ? getFundingCurrencyIcon(withdrawalDetails.bankCode)
      : undefined

  const fullDateWithTimeFormat = withTimeFormat(dateFormat)

  return (
    <motion.div
      className={styles.drawer}
      variants={drawerVariants}
      initial='initial'
      animate={controls}
    >
      {isMobile ? (
        <Box py='x12' borderBottom='outline-base-em' flexItem='0 0 auto'>
          <Modal.Header canClose>{transaction.type.name}</Modal.Header>
        </Box>
      ) : (
        <Box
          flex
          align='center'
          justify='space-between'
          gap='x8'
          py='x12'
          px='x20'
          borderBottom='outline-low-em'
          flexItem='0 0 auto'
        >
          <Title1 medium>{transaction.type.name}</Title1>
          <IconButton.Secondary size='sm' icon={CloseIcon} onClick={requestClose} />
        </Box>
      )}

      <Box flex direction='column' flexItem='1' minHeight={0} overflowY='auto'>
        <AnimatedSwitch
          wrapperClassName={styles.animatedSwitch}
          activeKey={transaction.id}
          variant='slide'
          activeIndex={currentIndex}
        >
          <Box flex direction='column' gap='x16' p={isMobile ? 'x16' : 'x20'}>
            <section className={styles.amountSection}>
              <SensitiveData>
                <Title1 semibold color='text-high-em'>
                  {amountSign}
                  <AnimatedNumber value={fields.amount} currency={fields.currency} />
                </Title1>
              </SensitiveData>

              <TransactionStatusItem
                transaction={transaction}
                isCancellable={isCancellable}
                onCancel={onCancelTransaction}
                subtitle={relativeLastUpdate}
              />
            </section>

            <Divider.Horizontal rounded />

            <section className={styles.section}>
              <Body1 medium className={styles.sectionTitle}>
                {t('Timeline')}
              </Body1>

              <AnimatedHeight>
                <Loading
                  isLoading={isHistoryLoading}
                  showLoadingIcon
                  renderText={() => null}
                  wrapperClass={styles.timelineLoader}
                >
                  <Box flex direction='column'>
                    {timelineItems.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <TransactionStatusItem
                          transactionHistoryItem={item}
                          subtitle={formatDate(item.createdDate, {
                            formatType: fullDateWithTimeFormat,
                          })}
                        />
                        {index !== timelineItems.length - 1 && (
                          <Box
                            flex
                            justify='center'
                            minWidth='var(--spacing-x32)'
                            maxWidth='var(--spacing-x32)'
                          >
                            <Divider.Vertical height={'var(--spacing-x18)'} />
                          </Box>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Loading>
              </AnimatedHeight>
            </section>

            <Divider.Horizontal rounded />

            <section className={styles.section}>
              <Body1 medium className={styles.sectionTitle}>
                {t('Details')}
              </Body1>

              <Box flex direction='column' gap='x12'>
                <Box flex align='center' justify='space-between' gap='x16'>
                  <span className={styles.detailLabel}>{t('TransactionHistory.Request Date')}</span>
                  <SensitiveData>
                    <span className={styles.detailValue}>
                      {formatDate(transaction.createdDate, {
                        formatType: fullDateWithTimeFormat,
                      })}
                    </span>
                  </SensitiveData>
                </Box>

                {fields.from && (
                  <Box flex align='center' justify='space-between' gap='x16'>
                    <span className={styles.detailLabel}>{t('From')}</span>
                    <Box flex justify='flex-end' textAlign='end'>
                      <SensitiveData>
                        <TransactionHistoryFromToCell
                          label={fromCellContent.label}
                          icon={fromCellData.icon}
                          subtitle={fromCellContent.subtitle}
                        />
                      </SensitiveData>
                    </Box>
                  </Box>
                )}

                {fields.to && (
                  <Box flex align='center' justify='space-between' gap='x16'>
                    <span className={styles.detailLabel}>{t('To')}</span>
                    <Box flex justify='flex-end' textAlign='end'>
                      <SensitiveData>
                        <TransactionHistoryFromToCell
                          label={toCellContent.label}
                          icon={toCellData.icon}
                          subtitle={toCellContent.subtitle}
                        />
                      </SensitiveData>
                    </Box>
                  </Box>
                )}

                {cardDetails && (
                  <Box flex align='center' justify='space-between' gap='x16'>
                    <span className={styles.detailLabel}>{t('Card')}</span>
                    <Box flex align='center' gap='x8'>
                      <CardBrandIcon background={false} />
                      <SensitiveData>
                        <span className={styles.detailValue}>
                          {`**** ${cardDetails.cardNumber.slice(-4)}`}
                        </span>
                      </SensitiveData>
                    </Box>
                  </Box>
                )}

                {exchangeRate && (
                  <Box flex align='center' justify='space-between' gap='x16'>
                    <span className={styles.detailLabelWithTooltip}>
                      <span>{t('Transaction exchange rate')}</span>
                      <Tooltip
                        heading={t('Transaction exchange rate')}
                        content={t(
                          'Amounts based on currencies which are denominated in foreign currencies'
                        )}
                        direction='top'
                      >
                        <ActionIcon icon={InfoIcon} size='x20' />
                      </Tooltip>
                    </span>
                    <SensitiveData>
                      <span className={styles.detailValue}>{formatNumber(exchangeRate)}</span>
                    </SensitiveData>
                  </Box>
                )}

                {originalAmount && (
                  <Box flex align='center' justify='space-between' gap='x16'>
                    <span className={styles.detailLabel}>{t('Original amount')}</span>
                    <SensitiveData>
                      <span className={styles.detailValue}>
                        {formatMoney(originalAmount.amount, originalAmount.currency)}
                      </span>
                    </SensitiveData>
                  </Box>
                )}

                {withdrawalDetails?.bankName && (
                  <Box flex align='center' justify='space-between' gap='x16'>
                    <span className={styles.detailLabel}>
                      {t(withdrawalDetails.isCrypto ? 'Digital Currency' : 'Bank Name')}
                    </span>
                    <Box flex align='center' justify='flex-end' gap='x8' textAlign='end'>
                      {DigitalCurrencyIcon && <DigitalCurrencyIcon size={24} />}
                      <SensitiveData>
                        <span className={styles.detailValue}>{withdrawalDetails.bankName}</span>
                      </SensitiveData>
                    </Box>
                  </Box>
                )}

                {withdrawalDetails?.externalAccount && (
                  <Box flex align='center' justify='space-between' gap='x16'>
                    <span className={styles.detailLabel}>
                      {t(withdrawalDetails.isCrypto ? 'Wallet Address' : 'Destination Account')}
                    </span>
                    <Box flex justify='flex-end' textAlign='end'>
                      <SensitiveData>
                        <span className={styles.detailValue}>
                          {withdrawalDetails.externalAccount}
                        </span>
                      </SensitiveData>
                    </Box>
                  </Box>
                )}

                <Box flex align='center' justify='space-between' gap='x16'>
                  <span className={styles.detailLabel}>{t('Wallet.Reference number')}</span>
                  <AnimatedHeight>
                    {transaction.referenceId ? (
                      <Box flex align='center' gap='x4'>
                        <SensitiveData>
                          <span className={styles.detailValue}>{transaction.referenceId}</span>
                        </SensitiveData>
                        <CopyButton
                          value={transaction.referenceId}
                          variant='compact'
                          size='x16'
                          tooltipText={isMobile ? undefined : t('Copy')}
                        />
                      </Box>
                    ) : (
                      <span className={styles.detailValueEmpty}>{t('N/A')}</span>
                    )}
                  </AnimatedHeight>
                </Box>
              </Box>
            </section>

            {statusAlert && (
              <AlertNotice id='transaction-status-alert' type='info' message={t(statusAlert)} />
            )}
          </Box>
        </AnimatedSwitch>
      </Box>

      <div className={styles.footer}>
        <Button.Secondary
          size={isMobile ? 'lg' : 'sm'}
          disabled={!hasPrevious}
          iconLeft={UndoUpLeftIcon}
          onClick={() => hasPrevious && setCurrentTransactionId(transactions[currentIndex - 1].id)}
        >
          {t('Previous')}
        </Button.Secondary>
        <Button.Secondary
          size={isMobile ? 'lg' : 'sm'}
          disabled={!hasNext}
          iconRight={UndoUpRightIcon}
          onClick={() => hasNext && setCurrentTransactionId(transactions[currentIndex + 1].id)}
        >
          {t('Next')}
        </Button.Secondary>
      </div>
    </motion.div>
  )
}

TransactionHistoryDrawerModal.containerMode = 'drawer'
